import { apiGet, apiPatch, apiDelete } from "../api-client"
import type { WellSummary } from './research'

/**
 * Normalize any API-number representation to the canonical 14-digit form.
 * Strips non-digits (handles hyphenated), then right-pads with '0' to 14 and
 * truncates to 14. A bare 10-digit API (e.g. "4246137917") becomes
 * "42461379170000". Use for all API-14 equality comparisons — a strict `===`
 * on unnormalized values misses matches (e.g. 10-digit row vs 14-digit route param).
 */
export function normalizeApi14(raw: string): string {
  const digits = (raw ?? '').replace(/\D/g, '')
  return digits.padEnd(14, '0').slice(0, 14)
}

export interface Pagination {
  total: number
  limit: number
  offset: number
  has_more: boolean
}

export interface TenantInteraction {
  has_interacted: boolean
  first_interaction_at: string
  last_interaction_at: string
  last_interaction_type: string
  interaction_count: number
  mode: string
  label: string
  owner_user_email: string
  metadata?: {
    plan_id?: string
    plugs_mode?: string
    snapshot_id?: string
  }
}

export interface WellHistory {
  id: number
  api14: string
  state: string
  county: string
  operator_name: string
  lease_name: string
  well_number: string
  field_name: string
  lat: number | null
  lon: number | null
  created_at: string
  updated_at: string
  tenant_interaction?: TenantInteraction
}

export interface WellsHistoryResponse {
  wells: WellHistory[]
  pagination: Pagination
}

// Well Filings Types
export type FormType = "W-3A" | "W-3" | "C-103" | "Sundry" | "GAU" | "W-15" | "W-2" | "H-5" | "H-15" | "Production Log" | "W-1"
export type FilingStatus = "draft" | "submitted" | "rejected" | "revised and submitted" | "approved" | "withdrawn"

export interface Filing {
  id: string
  form_type: FormType
  status: FilingStatus
  created_at: string
  updated_at: string
  metadata: Record<string, unknown>
  // Creator information (from Django Simple History)
  created_by?: string
  // Well information (included in all-filings endpoint)
  api14?: string
  lease_name?: string
  well_number?: string
  operator_name?: string
  county?: string
  state?: string
  // Workspace information
  workspace_id?: string | null
  workspace_name?: string | null
}

export interface WellFilingsResponse {
  api14?: string // Not included in all-filings endpoint
  total: number
  count: number
  next: string | null
  previous: string | null
  filings: Filing[]
}

/**
 * Fetch wells history for the current tenant
 * @param workspaceId - Optional workspace ID to filter wells by work-product association
 */
export async function fetchWellsHistory(workspaceId?: string | null): Promise<WellsHistoryResponse> {
  const params = new URLSearchParams()
  if (workspaceId) {
    params.set("workspace", workspaceId)
  }
  const queryString = params.toString()
  const url = `/api/tenant/wells/history/${queryString ? "?" + queryString : ""}`
  const response = await apiGet<WellsHistoryResponse>(url)
  return response
}

/**
 * Fetch filings for a specific well
 */
export interface FetchWellFilingsParams {
  form_type?: FormType[]
  status?: FilingStatus[]
  page?: number
  page_size?: number
  ordering?: string
}

export async function fetchWellFilings(
  api14: string,
  params?: FetchWellFilingsParams
): Promise<WellFilingsResponse> {
  const searchParams = new URLSearchParams()

  if (params?.form_type) {
    params.form_type.forEach((ft) => searchParams.append("form_type", ft))
  }

  if (params?.status) {
    params.status.forEach((s) => searchParams.append("status", s))
  }

  if (params?.page) {
    searchParams.set("page", params.page.toString())
  }

  if (params?.page_size) {
    searchParams.set("page_size", params.page_size.toString())
  }

  if (params?.ordering) {
    searchParams.set("ordering", params.ordering)
  }

  const queryString = searchParams.toString()
  const url = `/api/wells/${api14}/filings/${queryString ? "?" + queryString : ""}`

  const response = await apiGet<{
    count: number
    next: string | null
    previous: string | null
    results: WellFilingsResponse
  }>(url)
  
  // Extract the actual filings response from the pagination wrapper
  return response.results
}

/**
 * Fetch all filings across all wells (tenant-isolated)
 */
export interface FetchAllFilingsParams {
  form_type?: FormType[]
  status?: FilingStatus[]
  page?: number
  page_size?: number
  ordering?: string
  workspace?: string | null
}

export async function fetchAllFilings(
  params?: FetchAllFilingsParams
): Promise<WellFilingsResponse> {
  const searchParams = new URLSearchParams()

  if (params?.form_type) {
    params.form_type.forEach((ft) => searchParams.append("form_type", ft))
  }

  if (params?.status) {
    params.status.forEach((s) => searchParams.append("status", s))
  }

  if (params?.page) {
    searchParams.set("page", params.page.toString())
  }

  if (params?.page_size) {
    searchParams.set("page_size", params.page_size.toString())
  }

  if (params?.ordering) {
    searchParams.set("ordering", params.ordering)
  }

  if (params?.workspace) {
    searchParams.set("workspace", params.workspace)
  }

  const queryString = searchParams.toString()
  const url = `/api/filings/${queryString ? "?" + queryString : ""}`

  const response = await apiGet<{
    count: number
    next: string | null
    previous: string | null
    results: WellFilingsResponse
  }>(url)
  
  // Extract the actual filings response from the pagination wrapper
  return response.results
}

export interface WellComponent {
  id: string;
  component_type: string;
  component_name: string;
  layer: string;
  depth_from: number | null;
  depth_to: number | null;
  properties: Record<string, unknown>;
}

export interface WellComponentsResponse {
  api14: string
  total_components: number
  components: WellComponent[]
  geometry: {
    casing_strings: Array<{
      string_type?: string
      outside_dia_in?: number
      weight_ppf?: number
      grade?: string
      top_ft?: number
      bottom_ft?: number
      cement_top_ft?: number | null
      hole_size_in?: number
      _component_id?: string
      _layer?: string
    }>
    formation_tops: Array<{
      formation?: string
      top_ft?: number
      _component_id?: string
      _layer?: string
    }>
    perforations: Array<{
      top_ft?: number
      bottom_ft?: number
      formation?: string
    }>
    production_perforations: Array<{
      top_ft?: number
      bottom_ft?: number
      formation?: string
    }>
    tubing: Array<{
      size_in?: number
      top_ft?: number
      bottom_ft?: number
    }>
    liner: Array<{
      size_in?: number
      top_ft?: number
      bottom_ft?: number
    }>
    existing_tools: Array<{
      tool_type?: string
      depth_ft?: number
      top_ft?: number
      bottom_ft?: number
      source?: string
    }>
    mechanical_equipment: Array<{
      tool_type?: string
      type?: string
      depth_ft?: number
      top_ft?: number
      bottom_ft?: number
    }>
    historic_cement_jobs: Array<{
      job_type?: string
      interval_top_ft?: number
      interval_bottom_ft?: number
      cement_top_ft?: number
      sacks?: number
    }>
  }
}

export async function fetchWellComponentsWithGeometry(api14: string): Promise<WellComponentsResponse> {
  return apiGet<WellComponentsResponse>(`/api/tenant/wells/${api14}/components/`)
}

export async function fetchWellComponents(api14: string): Promise<WellComponent[]> {
  const response = await apiGet<WellComponentsResponse>(`/api/tenant/wells/${api14}/components/`)
  return response.components ?? []
}

/**
 * Fetch a single well's header info by API14 from the tenant registry
 */
export async function fetchWellByApi14(api14: string): Promise<WellHistory> {
  return apiGet<WellHistory>(`/api/tenant/wells/${api14}/`)
}

/**
 * Delete a draft filing. Routes to the correct endpoint by form type:
 * - W-3A / C-103: DELETE /api/plans/{plan_id}/  (id = metadata.plan_id UUID)
 * - W-3 / Sundry: DELETE /api/w3/forms/{id}/    (id = W3FormORM UUID)
 */
export async function deleteFilingDraft(id: string, formType: string): Promise<void> {
  const ft = (formType ?? '').toLowerCase().replace(/[-\s]/g, '')
  if (ft === 'w3a' || ft === 'c103') {
    await apiDelete<void>(`/api/plans/${id}/`)
  } else {
    await apiDelete<void>(`/api/w3/forms/${id}/`)
  }
}

/**
 * Move a draft filing to a different workspace
 * PATCH /api/w3/forms/{id}/ with { workspace: workspaceId }
 */
export async function moveFilingToWorkspace(
  filingId: string,
  workspaceId: string
): Promise<Filing> {
  return apiPatch<Filing>(`/api/w3/forms/${filingId}/`, { workspace: workspaceId })
}

/**
 * Fetch plan history for a specific well
 */
export async function fetchWellPlanHistory(api: string) {
  return apiGet(`/api/plans/${api}/history`)
}

export async function getWellWbdSync(api14: string): Promise<WellSummary> {
  return apiGet<WellSummary>(`/api/tenant/wells/${api14}/wbd-sync/`);
}

/**
 * Fetch a single well by API number from the wells history
 */
export async function fetchWell(api14: string): Promise<WellHistory> {
  console.log("[v0] Fetching well from history list for API:", api14)

  const response = await fetchWellsHistory()
  console.log("[v0] Wells history response:", response)

  // Find the well with matching api14
  const well = response.wells.find((w) => w.api14 === api14)

  if (!well) {
    console.error("[v0] Well not found in history for API:", api14)
    throw new Error(`Well not found: ${api14}`)
  }

  console.log("[v0] Found well in history:", well)
  console.log("[v0] Well metadata:", well.tenant_interaction?.metadata)

  return well
}
